import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { compose } from 'recompose';

import useToast from '../../../../../../hooks/useToast';
import ConfirmDialog from '../../../../../../components/ConfirmDialog';
import InputLabel from '../../../../../../../components/v2/InputLabel';
import RadioButtonGroup from '../../../../../../components/RadioButtonGroup';
import { PrimaryButton } from '../../../../../../../components/Button/ActionButtons';
import { Title as CardTitle } from '../../../../../../../components/v2/Card';

import { ToggleOption, ClientWeeklyJourneyMessagingToggleOptions } from '../../utils';
import {
  InputContainer,
  ButtonContainer,
  MasterOptionsWrapper,
  MasterOptionsDescription,
  HelperText,
} from './styles';
import texts from './texts.json';

const MasterConfigurations = ({
  isForceSendEnabled,
  setIsForceSendEnabled,
  coachConfig,
  handleClearAllMessages,
}) => {
  const [showClearAllModal, setShowClearAllModal] = useState(false);
  const { showToast } = useToast();

  const clearAllMessages = useCallback(async () => {
    await handleClearAllMessages();
    setShowClearAllModal(false);
    showToast(texts.clearAllSuccess);
  }, [handleClearAllMessages, showToast]);

  return (
    <MasterOptionsWrapper>
      <CardTitle>{texts.masterConfigurations}</CardTitle>
      <MasterOptionsDescription>{texts.masterOptionsDescription}</MasterOptionsDescription>
      {!!coachConfig?.clientJourneyWeeklyMessaging?.length && (
        <ButtonContainer>
          <PrimaryButton
            variant="warning"
            size="small"
            onClick={() => setShowClearAllModal(true)}
          >
            {texts.clearAllButton}
          </PrimaryButton>
        </ButtonContainer>
      )}
      {/* Hidden, not unmounted, until the coach config loads: the flag starts as false, so a coach with
          force send on would otherwise see the Disable state under the loading overlay. */}
      <InputContainer $isHidden={!coachConfig}>
        <InputLabel>{texts.forceSend}</InputLabel>
        <HelperText $isWarning={isForceSendEnabled}>
          {isForceSendEnabled ? texts.forceSendDescriptionOn : texts.forceSendDescriptionOff}
        </HelperText>
        <RadioButtonGroup
          options={ClientWeeklyJourneyMessagingToggleOptions}
          selectedOption={isForceSendEnabled ? ToggleOption.ENABLE : ToggleOption.DISABLE}
          onOptionChange={(value) => setIsForceSendEnabled(value === ToggleOption.ENABLE)}
        />
      </InputContainer>
      <ConfirmDialog
        isOpen={showClearAllModal}
        onConfirm={clearAllMessages}
        onCancel={() => setShowClearAllModal(false)}
        dialogTexts={{
          title: texts.clearAllTitle,
          content: texts.clearAllDescription,
          confirm: texts.clearAllButton,
          cancel: texts.cancel,
        }}
      />
    </MasterOptionsWrapper>
  );
};

MasterConfigurations.propTypes = {
  isForceSendEnabled: PropTypes.bool.isRequired,
  setIsForceSendEnabled: PropTypes.func.isRequired,
  coachConfig: PropTypes.object,
  handleClearAllMessages: PropTypes.func.isRequired,
};

MasterConfigurations.defaultProps = {
  coachConfig: null,
};

export default compose(
  observer,
)(MasterConfigurations);
